/* eslint-disable react-refresh/only-export-components */
/**
 * MilestoneEditorContext — Thin React bridge for the milestone-editor
 * orchestration service.
 *
 * Responsibilities:
 *  1. Supply the router-based `navigate` to the orchestrator's fallback path.
 *  2. Expose `useMilestoneEditor()` hook (returns `openEditor`, `closeEditor`, `session`).
 *  3. Expose `useRegisterMilestoneEditorHost()` hook for host pages.
 *
 * All actual host-routing logic lives in
 *   src/services/milestoneEditorOrchestrator.ts
 * so it can also be called from non-React code.
 */

import React, { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';

import type {
    OpenMilestoneEditorRequest,
    MilestoneEditorHost,
    MilestoneEditorHostType,
} from '@/types/milestoneEditor';
import {
    openMilestoneEditor,
    closeMilestoneEditor,
    getActiveMilestoneEditorSession,
    registerMilestoneEditorHost,
    setNavigationFallback,
    onEditorClosed,
} from '@/services/milestoneEditorOrchestrator';

// Re-export canonical types so existing imports keep working
export type { OpenMilestoneEditorRequest, MilestoneEditorHost } from '@/types/milestoneEditor';

/* ─── Provider ─────────────────────────────────────────────── */

/**
 * Mount once near the root (inside `<Router>`).
 * Supplies `useNavigate` to the orchestrator's fallback path.
 */
export const MilestoneEditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();

    useEffect(() => {
        setNavigationFallback((url: string) => navigate(url));
    }, [navigate]);

    return <>{children}</>;
};

/* ─── Consumer hooks ───────────────────────────────────────── */

// Micro external store so React components can subscribe to session changes
let sessionVersion = 0;
const sessionListeners = new Set<() => void>();

function subscribeSession(cb: () => void) {
    sessionListeners.add(cb);
    return () => { sessionListeners.delete(cb); };
}
function getSessionSnapshot() { return sessionVersion; }

// Bump version whenever session changes (open or close)
function notifySessionChange() {
    sessionVersion += 1;
    for (const cb of sessionListeners) cb();
}

// Auto-wire: listen to editor-closed events to bump version
let closedWired = false;
function ensureClosedWired() {
    if (closedWired) return;
    closedWired = true;
    onEditorClosed(() => notifySessionChange());
}

/**
 * Use from any component that wants to open/close the milestone editor
 * or read the active session.
 */
export function useMilestoneEditor() {
    ensureClosedWired();

    // Subscribe to session version so React re-renders on open/close
    useSyncExternalStore(subscribeSession, getSessionSnapshot);

    const openEditor = useCallback((request: OpenMilestoneEditorRequest) => {
        openMilestoneEditor(request);
        notifySessionChange();
    }, []);

    const closeEditor = useCallback(() => {
        closeMilestoneEditor();
        // notifySessionChange already called via onEditorClosed listener
    }, []);

    const session = getActiveMilestoneEditorSession();

    return { openEditor, closeEditor, session };
}

/**
 * Call from a host page to register a handler that fulfils edit requests.
 *
 * @param hostType  - `'project_detail'` or `'milestones'`
 * @param handler   - receives an `OpenMilestoneEditorRequest`, must return `true` if accepted
 * @param onClosed  - optional callback when the editor session closes
 */
export function useRegisterMilestoneEditorHost(
    hostType: MilestoneEditorHostType,
    handler: (request: OpenMilestoneEditorRequest) => boolean,
    onClosed?: (session: OpenMilestoneEditorRequest) => void,
) {
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    const onClosedRef = useRef(onClosed);
    onClosedRef.current = onClosed;

    useEffect(() => {
        const host: MilestoneEditorHost = {
            type: hostType,
            open: (req) => handlerRef.current(req),
            onEditorClosed: (session) => onClosedRef.current?.(session),
        };
        const unregister = registerMilestoneEditorHost(host);
        return unregister;
    }, [hostType]);
}
